import type { MeasureType } from '@highimpact/dashboard-types';
import { StateComponent } from '@highimpact/dashboard-ui';
import { forwardRef, LegacyRef, useMemo } from 'react';
import Plot from '../../../../internal/plot/Plot';
import { buildPlotlyLayoutFromElement } from '../../../../runtime/buildPlotlyLayoutFromElement';
import { getDefaultConfig, PlotProps } from '../../commons/plots/helpers';
import { getDefaultLayout } from '../../commons/plots/plotly-operations';
import { usePlotPngInstanceRegistration } from '../shared/usePlotPngInstanceRegistration';
import { useStaticElementMeasureRows } from '../shared/useStaticElementMeasureRows';

const directions = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
const colors = [
  '#f0dc62',
  '#f6bc41',
  '#f3921a',
  '#e86413',
  '#982608',
  '#000000',
];

function WindRosePlot(props: PlotProps, ref: LegacyRef<HTMLDivElement>) {
  const { dataset, elementData, dashboardElementId, widget_id } = props;
  const { rows, loading, error, usesElementMeasureRequest } =
    useStaticElementMeasureRows({
      element: elementData,
      plotType: 'windrose',
    });

  const plotName = elementData.name ?? 'Plot';
  usePlotPngInstanceRegistration({
    dashboardElementId,
    widgetId: widget_id,
    plotName,
    enabled: !(usesElementMeasureRequest && (loading || error)),
  });
  const layout = useMemo(
    () => getDefaultLayout(buildPlotlyLayoutFromElement(elementData)),
    [elementData]
  );

  const measureRows: MeasureType = usesElementMeasureRequest
    ? rows ?? []
    : dataset?.data ?? [];

  if (usesElementMeasureRequest && loading) {
    return (
      <div ref={ref} className="widget-content">
        <StateComponent
          name="Veuillez patienter !"
          message="Chargement en cours"
          spinner
        />
      </div>
    );
  }

  if (usesElementMeasureRequest && error) {
    return (
      <div ref={ref} className="widget-content">
        <div>Impossible de charger les données de la rose des vents.</div>
      </div>
    );
  }

  const dataset_map: { [KEY in string]: MeasureType } = {};

  measureRows.forEach((row) => {
    const speed = row['Vitesse du vent'] as string;

    if (!dataset_map[speed]) {
      dataset_map[speed] = [];
    }

    dataset_map[speed].push(row);
  });

  const speeds = Object.keys(dataset_map);
  speeds.sort(
    (a, b) =>
      parseInt((/^([0-9]+)-/.exec(a) || [])[1] || '0') -
      parseInt((/^([0-9]+)-[0-9]+$/.exec(b) || [])[1] || '0')
  );

  const traces: Plotly.Data[] = speeds.map((speed, color_index) => {
    const r_dataset = directions.map((direction) => {
      const row = dataset_map[speed]?.find(
        (row) => row['Direction du vent'] === direction
      );

      return row ? (row['Fréquence'] as number) : 0;
    });

    return {
      name: `${speed} km/h`,
      r: r_dataset,
      theta: directions,
      type: 'barpolar',
      marker: { color: colors[color_index] },
    };
  });

  return (
    <div ref={ref} className="widget-content">
      <Plot
        className="plot"
        layout={layout}
        data={traces}
        config={getDefaultConfig(plotName)}
      />
    </div>
  );
}

export default forwardRef(WindRosePlot);
